import { motion } from "framer-motion";
import {
  Mic,
  Film,
  Clapperboard,
  Image as ImageIcon,
  Smartphone,
  Linkedin,
  Twitter,
  Mail,
  Newspaper,
} from "lucide-react";
import SectionLabel from "./SectionLabel";

// Deliverables orbiting the central recording. Angles in degrees (0 = top).
const orbitItems = [
  { icon: Film, label: "Long Form Edit", angle: 0 },
  { icon: Clapperboard, label: "Trailers", angle: 45 },
  { icon: ImageIcon, label: "Thumbnails", angle: 90 },
  { icon: Smartphone, label: "Shorts & Reels", angle: 135 },
  { icon: Linkedin, label: "LinkedIn Posts", angle: 180 },
  { icon: Twitter, label: "X Threads", angle: 225 },
  { icon: Mail, label: "Newsletter", angle: 270 },
  { icon: Newspaper, label: "Blog Articles", angle: 315 },
];

const RADIUS = 330; // px from center on desktop

const toXY = (deg) => {
  const rad = ((deg - 90) * Math.PI) / 180;
  return { x: Math.cos(rad) * RADIUS, y: Math.sin(rad) * RADIUS };
};

const Chip = ({ icon: Icon, label, i, active = true }) => (
  <motion.div
    initial={{ scale: 0.85 }}
    whileInView={{ scale: 1 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.5, delay: 0.15 + i * 0.07, ease: [0.16, 1, 0.3, 1] }}
    className={`group flex items-center gap-2.5 rounded-full border backdrop-blur-sm pl-2 pr-4 py-2 whitespace-nowrap transition-all duration-500 ${
      active
        ? "border-[#F4C75E]/70 bg-[#0D0D0D] shadow-[0_0_24px_rgba(244,199,94,0.18)]"
        : "border-[#2a2a2a] bg-[#0A0A0A]/90 opacity-45"
    }`}
  >
    <span
      className={`w-8 h-8 rounded-full border flex items-center justify-center transition-all duration-500 shrink-0 ${
        active
          ? "border-[#F4C75E] bg-[#F4C75E] text-black"
          : "border-[#3a3a3a] text-[#8a8a8a]"
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
    </span>
    <span
      className={`text-xs md:text-sm font-semibold tracking-wide transition-colors duration-500 ${
        active ? "text-[#F5F5F5]" : "text-[#8a8a8a]"
      }`}
    >
      {label}
    </span>
  </motion.div>
);

export default function Flywheel() {
  return (
    <section
      id="flywheel"
      data-testid="flywheel-section"
      className="relative px-6 md:px-16 lg:px-32 py-24 md:py-32 bg-[#050505]"
    >
      {/* Ambient gold glow */}
      <div
        aria-hidden
        className="hidden md:block absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 45% 38% at 50% 58%, rgba(244,199,94,0.09), transparent 70%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-8">
          <div className="lg:col-span-7">
            <SectionLabel number="05" label="The Media Engine" testId="flywheel-label" />
            <h2 className="font-display font-bold uppercase text-2xl md:text-4xl tracking-tighter leading-[0.95] [word-spacing:0.18em]">
              One recording in.<br />
              <span className="text-[#F4C75E]">A full media stack out.</span>
            </h2>
          </div>
          <p className="lg:col-span-5 text-[#A3A3A3] text-sm md:text-base leading-relaxed">
            Every conversation becomes a repeatable engine of assets that keeps
            compounding across platforms, so one episode works for your brand
            for weeks, not an afternoon.
          </p>
        </div>

        {/* ===== Desktop: orbital engine (static, all assets lit) ===== */}
        <div className="hidden md:block relative h-[720px] xl:h-[780px]">
          <div
            data-testid="flywheel-visual"
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 origin-center scale-[0.78] xl:scale-[0.92]"
            style={{ width: 900, height: 900 }}
          >
              {/* Spokes, rings and marker dots */}
              <svg
                aria-hidden
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                width="720"
                height="720"
                viewBox="0 0 720 720"
                fill="none"
              >
                <defs>
                  <radialGradient id="spokeMaskGrad" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="white" stopOpacity="0" />
                    <stop offset="28%" stopColor="white" stopOpacity="0.55" />
                    <stop offset="100%" stopColor="white" stopOpacity="1" />
                  </radialGradient>
                  <mask id="spokeFade">
                    <rect width="720" height="720" fill="url(#spokeMaskGrad)" />
                  </mask>
                </defs>
                <circle cx="360" cy="360" r="330" stroke="#F4C75E" strokeOpacity="0.14" />
                <circle cx="360" cy="360" r="225" stroke="#F4C75E" strokeOpacity="0.08" />
                <g mask="url(#spokeFade)">
                  {orbitItems.map((it) => {
                    const rad = ((it.angle - 90) * Math.PI) / 180;
                    return (
                      <line
                        key={it.label}
                        x1={360 + Math.cos(rad) * 105}
                        y1={360 + Math.sin(rad) * 105}
                        x2={360 + Math.cos(rad) * 328}
                        y2={360 + Math.sin(rad) * 328}
                        stroke="#F4C75E"
                        strokeOpacity="0.28"
                        strokeWidth="1"
                      />
                    );
                  })}
                </g>
                {orbitItems.map((it) => {
                  const rad = ((it.angle - 90) * Math.PI) / 180;
                  return (
                    <circle
                      key={it.label}
                      cx={360 + Math.cos(rad) * 330}
                      cy={360 + Math.sin(rad) * 330}
                      r="4"
                      fill="#F4C75E"
                      fillOpacity="0.9"
                    />
                  );
                })}
              </svg>

              {/* Center hub: the recording */}
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                <div aria-hidden className="absolute -inset-3 rounded-full border border-[#F4C75E]/25" />
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                  data-testid="flywheel-center"
                  className="relative flex flex-col items-center justify-center w-[190px] h-[190px] rounded-full bg-gradient-to-br from-[#F4C75E] to-[#B6873A] text-black shadow-[0_0_70px_rgba(244,199,94,0.3)]"
                >
                  <Mic className="w-7 h-7 mb-2" />
                  <span className="font-display text-[9px] uppercase tracking-[0.3em] font-bold opacity-70">
                    The Input
                  </span>
                  <span className="font-display text-base md:text-lg font-bold uppercase tracking-tight mt-1 whitespace-nowrap">
                    Your Recording
                  </span>
                </motion.div>
              </div>

              {/* Orbiting deliverable chips — always highlighted */}
              {orbitItems.map((item, i) => {
                const { x, y } = toXY(item.angle);
                return (
                  <div
                    key={item.label}
                    data-testid={`flywheel-chip-${i}`}
                    className="absolute left-1/2 top-1/2"
                    style={{ transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))` }}
                  >
                    <Chip icon={item.icon} label={item.label} i={i} />
                  </div>
                );
              })}
            </div>
          </div>

        {/* ===== Mobile: hub + stacked chips ===== */}
        <div className="md:hidden flex flex-col items-center gap-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            data-testid="flywheel-center-mobile"
            className="flex flex-col items-center justify-center w-[190px] h-[190px] rounded-full bg-gradient-to-br from-[#F4C75E] to-[#B6873A] text-black shadow-[0_0_60px_rgba(244,199,94,0.3)]"
          >
            <Mic className="w-7 h-7 mb-2" />
            <span className="font-display text-[9px] uppercase tracking-[0.3em] font-bold opacity-70">
              The Input
            </span>
            <span className="font-display text-lg font-bold uppercase tracking-tight mt-1 whitespace-nowrap">
              Your Recording
            </span>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-2.5">
            {orbitItems.map((item, i) => (
              <Chip key={item.label} icon={item.icon} label={item.label} i={i} />
            ))}
          </div>
        </div>

        {/* Footer caption */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-14 md:mt-16 flex flex-col sm:flex-row items-center justify-center gap-4 text-xs uppercase tracking-[0.3em] text-[#666]"
        >
          <span>Record Once</span>
          <span aria-hidden className="hidden sm:inline-block w-10 h-px bg-[#F4C75E]/40" />
          <span className="text-[#F4C75E]">The Engine Runs It Everywhere</span>
          <span aria-hidden className="hidden sm:inline-block w-10 h-px bg-[#F4C75E]/40" />
          <span>Your Show Keeps Compounding</span>
        </motion.div>
      </div>
    </section>
  );
}
